import { forwardRef, useState } from 'react'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { ReadonlyDeep } from 'type-fest'

import { ProposalAdvisorConversationAvatar } from './ProposalAdvisorConversationAvatar'
import { ProposalAdvisorConversationMessageActionsDropdown } from './ProposalAdvisorConversationMessageActionsDropdown'
import { useDateFormatter } from '../../../../lib/hooks'
import { cn } from '../../../../shadcn/utils'

type ProposalAdvisorConversationMessageProps = ReadonlyDeep<{
  message: {
    sentAt: Date
    content?: string
    color: 'grey' | 'turquoise'
    author: {
      profilePicture?: string
      firstname: string
      lastname: string
      hideName?: boolean
    }
  }
  className?: string
  flexOrientation: 'right-to-left' | 'left-to-right'
  onDelete?: () => void
}>

function useMessageTimeFormatter() {
  const formatter = useDateFormatter()
  formatter.updateTemplate('time', {
    minute: 'numeric',
    hour: 'numeric',
  })
  return (date: Date) =>
    formatter.format({
      date,
      formatString: 'time',
    })
}

export const ProposalAdvisorConversationMessage = forwardRef<
  HTMLDivElement,
  ProposalAdvisorConversationMessageProps
>(function ProposalAdvisorConversationMessage(
  { message, className, flexOrientation, onDelete },
  ref,
) {
  const formatTime = useMessageTimeFormatter()
  const [hovered, setHovered] = useState(false)

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        'flex gap-2',
        flexOrientation === 'right-to-left' ? 'flex-row-reverse' : 'flex-row',
        className,
      )}
    >
      <ProposalAdvisorConversationAvatar
        firstname={message.author.firstname}
        profilePicture={message.author.profilePicture}
      />
      <div
        className={cn(
          'flex flex-col gap-1 overflow-hidden rounded-xl px-3 py-2 text-sm',
          (() => {
            switch (message.color) {
              case 'grey':
                return 'bg-grey-300'
              case 'turquoise':
                return 'bg-turquoise-100'
            }
          })(),
        )}
      >
        {message.author.hideName !== true && (
          <header className='text-grey-700'>
            {message.author.firstname} {message.author.lastname}
          </header>
        )}
        <p
          className={cn(
            'break-words',
            message.content === undefined && 'italic text-grey-700',
          )}
        >
          {message.content ?? (
            <FormattedMessage
              defaultMessage='message supprimé'
              id='ui.ProposalAdvisorConversationMessage.deletedMessage'
            />
          )}
        </p>
        <time
          className='self-end text-grey-700'
          dateTime={formatTime(message.sentAt)}
        >
          {formatTime(message.sentAt)}
        </time>
      </div>
      {onDelete && (
        <ProposalAdvisorConversationMessageActionsDropdown
          menuOrientation={
            flexOrientation === 'right-to-left' ? 'left' : 'right'
          }
          onDelete={() => onDelete()}
          show={hovered}
          className='self-center'
        />
      )}
    </div>
  )
})
